import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

/**
 * Configuration for standalone scene viewing
 */
export interface StandaloneConfig {
  cameraPosition?: THREE.Vector3;
  cameraTarget?: THREE.Vector3;
  showGrid?: boolean;
  showAxes?: boolean;
  gridSize?: number;
  backgroundColor?: number;
  enableControls?: boolean;
}

/**
 * Controls camera and scene positioning for standalone (non-AR) mode
 */
export class StandaloneController {
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls | null = null;
  private gridHelper: THREE.GridHelper | null = null;
  private axesHelper: THREE.AxesHelper | null = null;
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;

  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer, config: StandaloneConfig = {}) {
    this.scene = scene;
    this.renderer = renderer;

    // Create standard perspective camera
    this.camera = new THREE.PerspectiveCamera(
      50, // FOV
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );

    // Set camera position
    const camPos = config.cameraPosition || new THREE.Vector3(0, 2, 5);
    this.camera.position.copy(camPos);

    // Setup orbit controls if enabled
    if (config.enableControls !== false) {
      this.setupControls(config.cameraTarget);
    }

    // Setup helpers
    if (config.showGrid) {
      this.addGridHelper(config.gridSize);
    }

    if (config.showAxes) {
      this.addAxesHelper();
    }

    // Set background color
    if (config.backgroundColor !== undefined) {
      this.scene.background = new THREE.Color(config.backgroundColor);
    } else {
      this.scene.background = new THREE.Color(0x333333);
    }

    // Add ambient light for visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);

    // Add directional light
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(5, 10, 7.5);
    this.scene.add(dirLight);

    // Handle window resize
    window.addEventListener('resize', () => this.onWindowResize());

    console.log('✅ Standalone controller initialized');
    console.log('   Camera position:', this.camera.position);
    console.log('   Controls enabled:', this.controls !== null);
  }

  private setupControls(target?: THREE.Vector3): void {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 0.5;
    this.controls.maxDistance = 50;

    if (target) {
      this.controls.target.copy(target);
    }

    this.controls.update();
    console.log('🎮 Orbit controls enabled');
  }

  private addGridHelper(size: number = 10): void {
    this.gridHelper = new THREE.GridHelper(size, size);
    this.scene.add(this.gridHelper);
    console.log('📐 Grid helper added');
  }

  private addAxesHelper(): void {
    this.axesHelper = new THREE.AxesHelper(2);
    this.scene.add(this.axesHelper);
    console.log('🎯 Axes helper added (RGB = XYZ)');
  }

  private onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Update controls - call this in your render loop
   */
  update(): void {
    if (this.controls) {
      this.controls.update();
    }
  }

  /**
   * Get the camera
   */
  getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  /**
   * Get the controls
   */
  getControls(): OrbitControls | null {
    return this.controls;
  }

  /**
   * Set camera position
   */
  setCameraPosition(x: number, y: number, z: number): void {
    this.camera.position.set(x, y, z);
    if (this.controls) {
      this.controls.update();
    }
    console.log(`📷 Camera position set to (${x}, ${y}, ${z})`);
  }

  /**
   * Set camera target (what it's looking at)
   */
  setCameraTarget(x: number, y: number, z: number): void {
    if (this.controls) {
      this.controls.target.set(x, y, z);
      this.controls.update();
      console.log(`🎯 Camera target set to (${x}, ${y}, ${z})`);
    }
  }

  /**
   * Toggle grid visibility
   */
  toggleGrid(visible?: boolean): void {
    if (this.gridHelper) {
      this.gridHelper.visible = visible !== undefined ? visible : !this.gridHelper.visible;
      console.log(`📐 Grid ${this.gridHelper.visible ? 'shown' : 'hidden'}`);
    } else if (visible !== false) {
      this.addGridHelper();
    }
  }

  /**
   * Toggle axes visibility
   */
  toggleAxes(visible?: boolean): void {
    if (this.axesHelper) {
      this.axesHelper.visible = visible !== undefined ? visible : !this.axesHelper.visible;
      console.log(`🎯 Axes ${this.axesHelper.visible ? 'shown' : 'hidden'}`);
    } else if (visible !== false) {
      this.addAxesHelper();
    }
  }

  /**
   * Reset camera to initial position
   */
  resetCamera(): void {
    this.camera.position.set(0, 2, 5);
    if (this.controls) {
      this.controls.target.set(0, 0, 0);
      this.controls.update();
    }
    console.log('📷 Camera reset to default position');
  }

  /**
   * Focus camera on a specific point
   */
  focusOn(position: THREE.Vector3, distance: number = 5): void {
    if (this.controls) {
      // Calculate camera position at specified distance from target
      const direction = new THREE.Vector3()
        .subVectors(this.camera.position, this.controls.target)
        .normalize();

      this.camera.position.copy(position).add(direction.multiplyScalar(distance));
      this.controls.target.copy(position);
      this.controls.update();

      console.log(`🎯 Camera focused on (${position.x}, ${position.y}, ${position.z})`);
    }
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    if (this.controls) {
      this.controls.dispose();
    }

    if (this.gridHelper) {
      this.scene.remove(this.gridHelper);
    }

    if (this.axesHelper) {
      this.scene.remove(this.axesHelper);
    }

    window.removeEventListener('resize', () => this.onWindowResize());
  }
}
